import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  VideoHTMLAttributes,
} from 'react'
import { useTranslation } from 'react-i18next'

import * as useHooks from 'usehooks-ts'

import { loadUserChoices } from '@livekit/components-core'
import type { ParticipantClickEvent, TrackReference } from '@livekit/components-core'
import { LocalVideoTrack, RemoteTrackPublication, Track } from 'livekit-client'

import { useEnsureTrackRef } from '@/src/context'

import { useMediaTrackBySourceOrName } from '@/src/hooks/room/track/useMediaTrackBySourceOrName'
import { useToggleTrackProcessor } from '@/src/hooks/room/track/useToggleTrackProcessor'

/** @public */
export interface VideoTrackProps extends VideoHTMLAttributes<HTMLVideoElement> {
  /** The track reference of the track to render. */
  trackRef?: TrackReference
  onTrackClick?: (evt: ParticipantClickEvent) => void
  onSubscriptionStatusChanged?: (subscribed: boolean) => void
  manageSubscription?: boolean
  isPiP?: boolean
}

const intersectionObserverOptions = {}

/**
 * The `VideoTrack` component is responsible for rendering participant video tracks like `camera` and `screen_share`.
 * This component must have access to the participant's context, or alternatively pass it a `Participant` as a property.
 *
 * @example
 * ```tsx
 * <VideoTrack trackRef={trackRef} />
 * ```
 * @see {@link @livekit/components-react#ParticipantTile | ParticipantTile}
 * @public
 */
export const VideoTrack = /* @__PURE__ */ forwardRef<HTMLVideoElement, VideoTrackProps>(
  function VideoTrack(
    {
      onTrackClick,
      onClick,
      onSubscriptionStatusChanged,
      trackRef,
      manageSubscription,
      isPiP,
      ...props
    }: VideoTrackProps,
    ref,
  ) {
    const { t } = useTranslation()
    const [showLocalCameraVideo, setShowLocalCameraVideo] = useState(true)
    const trackReference = useEnsureTrackRef(trackRef)

    const mediaEl = useRef<HTMLVideoElement>(null)
    useImperativeHandle(ref, () => mediaEl.current)

    const intersectionEntry = useHooks.useIntersectionObserver(mediaEl, intersectionObserverOptions)

    const debouncedIntersectionEntry = useHooks.useDebounce(intersectionEntry, 3000)
    //@ts-ignore-next-line вынести интерфейс из react core
    const bgEffect = loadUserChoices()?.currentBgEffect as BgEffect

    useEffect(() => {
      if (
        trackReference.source === Track.Source.Camera &&
        bgEffect &&
        trackReference.participant.isLocal
      ) {
        setShowLocalCameraVideo(false)
        const setProcessor = async () => {
          await useToggleTrackProcessor(
            trackReference.publication.videoTrack as LocalVideoTrack,
            bgEffect,
          ).then(() => {
            setShowLocalCameraVideo(true)
          })
        }
        setProcessor()
      }
    }, [])

    useEffect(() => {
      if (
        manageSubscription &&
        trackReference.publication instanceof RemoteTrackPublication &&
        debouncedIntersectionEntry?.isIntersecting === false &&
        intersectionEntry?.isIntersecting === false
      ) {
        trackReference.publication.setSubscribed(false)
      }
    }, [
      debouncedIntersectionEntry,
      trackReference,
      manageSubscription,
      intersectionEntry?.isIntersecting,
    ])

    useEffect(() => {
      if (
        manageSubscription &&
        trackReference.publication instanceof RemoteTrackPublication &&
        intersectionEntry?.isIntersecting === true
      ) {
        trackReference.publication.setSubscribed(true)
      }
    }, [intersectionEntry, trackReference, manageSubscription])

    useEffect(() => {
      if (!manageSubscription) return

      const publication = trackReference.publication

      if (!(publication instanceof RemoteTrackPublication)) return

      return () => {
        publication.setSubscribed(false)
      }
    }, [manageSubscription, trackReference])

    const mediaTrackBySourceOrNameOptions = useMemo(
      () => ({ element: mediaEl, props }),
      [mediaEl, props],
    )

    const {
      elementProps,
      publication: pub,
      isSubscribed,
    } = useMediaTrackBySourceOrName(trackReference, mediaTrackBySourceOrNameOptions, isPiP)

    useEffect(() => {
      onSubscriptionStatusChanged?.(!!isSubscribed)
    }, [isSubscribed, onSubscriptionStatusChanged])

    const clickHandler = (evt: React.MouseEvent<HTMLVideoElement, MouseEvent>) => {
      onClick?.(evt)
      onTrackClick?.({ participant: trackReference?.participant, track: pub })
    }

    if (trackRef.participant.isLocal === true && trackRef.source === Track.Source.ScreenShare) {
      return (
        <div style={{ opacity: '0.95', height: '100%' }}>
          <div className="local-screen-share">{t('screenShareInfo')}</div>
          <video ref={mediaEl} {...elementProps} muted={true} onClick={clickHandler}>
            <track
              src="/captions/captions_ru.vtt"
              kind="captions"
              srcLang="ru"
              label="Russian captions"
            />
            <track
              src="/captions/captions_en.vtt"
              kind="captions"
              srcLang="en"
              label="English captions"
            />
          </video>
        </div>
      )
    }
    return (
      <video
        {...elementProps}
        ref={mediaEl}
        style={{ display: showLocalCameraVideo ? '' : 'none' }}
        muted={true}
        onClick={clickHandler}
      />
    )
  },
)
